import React from 'react';
import { useTable } from 'chameleon/ui-stack/ui/base/table';
import TextWithTooltip from 'chameleon/ui-stack/ui/assessments/text-with-tooltip';
import Button from 'chameleon/ui-stack/ui/base/button';
import { Icon } from 'legacy-packages/ft-cb';
import ScoredIcon from 'chameleon/ui-stack/ui/assessments/scored-icon';
import dayjs from 'dayjs';
import { DEFAULT_STUDENT } from 'legacy-src/utility/Filters';
import Tooltip from 'chameleon/ui-stack/ui/base/tooltip';
import BaseIcon from 'chameleon/ui-stack/ui/base/icon';

import './Table.scss';
import usePerformanceTiers, {
  getTierColorClasses,
} from 'chameleon/src/hooks/usePerformanceTiers';
import get from 'lodash/get';
import cn from 'classnames';
import CustomCell from './CustomCell';
import _ from 'lodash';

const Table = ({
  data,
  router,
  resourceType,
  filterProps,
  immediateFeedbackFlag,
}) => {
  let { assignments, videos } = data;
  const { activeSection, activeEducationPeriod, activeStudent, sections } =
    filterProps;

  const subjectId = router.params.subject;

  const { performanceTiers } = usePerformanceTiers(subjectId);

  // no class message
  if (sections && sections.length === 0) {
    return (
      <p className="px-5 py-4 mt-6 text-2xl leading-8 bg-gray-100 border border-gray-500 border-dashed w-100 rounded-xl">
        There are no classes for this subject for the selected academic year.
      </p>
    );
  }

  // default filter params that will be applied in future
  const showAssignmentWithNoResult = false;
  const showVideoNotAssigned = false;
  const showVideoWithNoResult = false;
  // filter assignment and video data base on filter param
  if (!showAssignmentWithNoResult) {
    assignments = assignments.filter(
      (assignment) =>
        (assignment.is_spp &&
          Number.parseFloat(assignment.average_score) > 0) ||
        (!assignment.is_spp && assignment.average_score !== null),
    );
  }
  if (!showVideoNotAssigned) {
    videos = videos.filter((video) => video.assigned);
  }
  if (!showVideoWithNoResult) {
    videos = videos.filter((video) => video.watched > 0 || video.started > 0);
  }

  // need to be mutually exclusive
  if (resourceType === 'ppc') {
    assignments = assignments.filter(
      (assignment) => assignment.is_ppc && !assignment.is_pe,
    );
    videos = [];
  } else if (resourceType === 'qb') {
    assignments = assignments.filter(
      (assignment) =>
        !assignment.is_ppc && !assignment.is_pe && !assignment.is_spp,
    );
    videos = [];
  } else if (resourceType === 'pe') {
    assignments = assignments.filter((assignment) => assignment.is_pe);
    videos = [];
  } else if (resourceType === 'video') {
    assignments = [];
  } else if (resourceType === 'practice-problems') {
    assignments = assignments.filter((assignment) => assignment.is_spp);
    videos = [];
  } else if (resourceType === 'learning-checkpoint') {
    assignments = assignments.filter(
      (assignment) => assignment.is_learning_checkpoint,
    );
    videos = [];
  } else if (resourceType === 'performance-task') {
    assignments = assignments.filter(
      (assignment) => assignment.is_performance_task,
    );
    videos = [];
  } else if (resourceType === 'teacher-authored') {
    assignments = assignments.filter(
      (assignment) =>
        !assignment.is_learning_checkpoint && !assignment.is_performance_task,
    );
    videos = [];
  }

  const assignmentdById = assignments.reduce(
    (accum, assignment) => ({
      ...accum,
      [assignment.id]: assignment,
    }),
    {},
  );

  let rowsData = data.rows || [];
  // filter student data if there is an active student
  if (activeStudent && activeStudent.value !== DEFAULT_STUDENT.value) {
    const activeStudentId = activeStudent.value.split('-')[1];
    rowsData = rowsData.filter(
      (row) => row.student.id.toString() === activeStudentId,
    );
  }
  rowsData = _.sortBy(rowsData, ['student.last_name', 'student.first_name']);

  const assignmentHeaderImage = (item) => {
    if (item.is_pe) {
      return <div className="text-green-500 top_border" />;
    } else if (item.is_ppc || item.is_learning_checkpoint) {
      return <div className="text-blue-600 top_border" />;
    } else if (item.is_spp) {
      return <div className="text-teal-600 top_border" />;
    } else if (item.is_performance_task) {
      return <div className="text-blue-400 top_border" />;
    }
    return <div className="text-orange-500 top_border" />;
  };

  const ImmediateFeedbackIcon = ({ isEllipsis }) => {
    const tooltipProps = {
      description: 'Immediate Feedback ON',
    };
    const ellipsisProps = isEllipsis
      ? { containerClassName: 'tooltip-wrapper' }
      : {};

    return (
      <Tooltip
        description="Immediate Feedback ON"
        {...tooltipProps}
        {...ellipsisProps}
      >
        <BaseIcon name="immediate_feedback" />
      </Tooltip>
    );
  };

  const renderEllipsisTitle = (title, immediateFeedback, isVideo) => {
    const renderImmediateFeedbackIcon =
      immediateFeedbackFlag && immediateFeedback;

    return (
      <div className={cn({ 'px-1': true, 'ellipsis-wrapper': isVideo })}>
        {title.length >= 30 ? (
          <TextWithTooltip
            renderInBody
            target={title}
            ariaLabel={title}
            tooltip={title}
            className="title-wrapper-tooltip"
          >
            <p className="title_ellipsis">{title}</p>
            {!!renderImmediateFeedbackIcon && (
              <ImmediateFeedbackIcon isEllipsis />
            )}
          </TextWithTooltip>
        ) : (
          <p className="h-11 title-wrapper">
            {title} {!!renderImmediateFeedbackIcon && <ImmediateFeedbackIcon />}
          </p>
        )}
      </div>
    );
  };

  const renderRedirectIndicator = () => {
    return (
      <Button className="flex items-center w-full text-blue-700 bg-gray-100 hover:bg-blue-600 hover:bg-opacity-10">
        <div className="flex ml-3 text-left">Results Overview</div>
        <Icon name="chevron_right" className="fill-current" size="12px" />
      </Button>
    );
  };

  const renderStartDueDate = (item) => {
    const { starts_at: startsAt, due_at: dueAt } = item;
    const startDate =
      startsAt && startsAt !== 'Z'
        ? dayjs(startsAt).local().format('M/D')
        : '-';
    const dueDate =
      dueAt && dueAt !== 'Z' ? dayjs(dueAt).local().format('M/D') : '-';
    return (
      <div className="flex justify-center border-t border-gray-300">
        <div className="mr-20">
          <p className="h-2 text-12">START</p>
          <p className="h-2 text-14">{startDate}</p>
        </div>
        <div>
          <p className="h-2 text-12">DUE</p>
          <p className="h-2 text-14">{dueDate}</p>
        </div>
      </div>
    );
  };

  const assignmentTitleOnClick = (item) => {
    /******************************************************************************
     * viewResultOverviewFromReportPage is set here so that it can be used to decide
     * if active or filterControl-selected education period should be used as the education period
     * When loadAssignments function is called in ClassResultsContainer.
     ******************************************************************************************/
    const {
      location: { state: currentRouterState },
    } = router;
    router.push({
      pathname: `/${subjectId}/assessments/results/${item.id}`,
      state: { ...currentRouterState, viewResultOverviewFromReportPage: true },
    });
  };

  const getassignmentCellRedirectUrl = (item) => {
    const assignmentId = item.column.parent.id;
    const studentId = item.row.original.id;
    const isSpp = get(item, `row.original[a-${assignmentId}].isSpp`, false);

    const { location } = router || {};
    const { pathname, search } = location || {};
    const returnPath = `${pathname}${search}`;
    const state = isSpp ? { returnPath } : {};

    if (isSpp) {
      return {
        redirectUrl: `/${subjectId}/assessments/spp/review/${assignmentId}/students/${studentId}`,
        state,
      };
    }

    return {
      redirectUrl: `/${subjectId}/assessments/results/${assignmentId}/students/${studentId}/`,
      state,
    };
  };

  const headerClassName =
    'align-top border-r border-b border-gray-300 text-14 text-gray-600 bg-gray-100 font-normal text-center';

  const studentHeaders = {
    Header: (
      <div className="my-16 w-150">
        <p className="text-gray-600 text-12">
          {(activeSection && activeSection.label) || 'Selected Class'}
        </p>
        <p className="leading-10 text-18">
          Full Year
          <br />
          {activeEducationPeriod && activeEducationPeriod.label}
        </p>
      </div>
    ),
    id: 'class-filter-info',
    className:
      'align-top border-r border-b bg-gray-100 border-gray-300 text-center',
    columns: [
      {
        Header: 'Student Name',
        accessor: 'studentName',
        className:
          'flex border-r border-b bg-gray-100 border-gray-300 py-8 pl-8',
      },
    ],
  };

  const assignmentHeaders = assignments
    .filter(({ is_spp: isSpp }) => !isSpp)
    .map((item) => ({
      Header: (
        <div className="w-60">
          {assignmentHeaderImage(item)}
          <div className="cursor-pointer assignment-header-wrapper">
            {renderEllipsisTitle(item.title, item.has_immediate_feedback)}
          </div>
          <div onClick={() => assignmentTitleOnClick(item)}>
            {renderRedirectIndicator(true)}
          </div>
          {renderStartDueDate(item)}
        </div>
      ),
      id: item.id,
      startDate: item.starts_at,
      className: headerClassName,
      columns: [
        {
          Header:
            item.average_score !== null && item.max_score !== null
              ? item.max_score
                ? `Class Avg: ${(
                    (item.average_score / item.max_score) *
                    100
                  ).toFixed(0)}%
                Points: ${item.average_score}/${item.max_score}`
                : `N/A ${item.average_score}/${item.max_score}`
              : 'N/A',
          accessor: `a-${item.id}`,
          className:
            'border-r border-b bg-black text-white text-opacity-75 text-center border-gray-300 font-normal pre-line class_avg_col',
        },
      ],
    }));
  const sppHeaders = assignments
    .filter(({ is_spp: isSpp }) => isSpp)
    .map((item) => ({
      Header: (
        <div className="w-60">
          {assignmentHeaderImage(item)}
          <div className="cursor-pointer assignment-header-wrapper">
            {renderEllipsisTitle(item.title, item.has_immediate_feedback)}
          </div>
          <div onClick={() => assignmentTitleOnClick(item)}>
            {renderRedirectIndicator(true)}
          </div>
          {renderStartDueDate(item)}
        </div>
      ),
      id: item.id,
      startDate: item.starts_at,
      className: headerClassName,
      columns: [
        {
          Header: `Submitted: ${item.submitted_percent.toFixed(0)}%
          Fully Answered: ${item.fully_answered_percent.toFixed(0)}%`,
          accessor: `a-${item.id}`,
          className:
            'border-r border-b bg-black text-white text-opacity-75 text-center border-gray-300 font-normal pre-line',
        },
      ],
    }));
  const studentCount = rowsData.length;
  const videoHeaders = videos.map((item) => ({
    Header: (
      <div className="w-60">
        <div className="text-purple-600 top_border" />
        {renderEllipsisTitle(
          item.title || `${item.unitId}.${item.resourceId}: Daily Video`,
          item.has_immediate_feedback,
          true,
        )}
        <div className="opacity-0 h-12.2" />
        {renderStartDueDate(item)}
      </div>
    ),
    id: `dv-${item.id}`,
    startDate: item.starts_at,
    className: headerClassName,
    columns: [
      {
        Header:
          item.watched >= 0 && studentCount > 0
            ? `Fully Watched: ${((item.watched / studentCount) * 100).toFixed(
                0,
              )}%`
            : 'N/A',
        accessor: `dv-${item.id}`,
        className:
          'border-r border-b bg-black text-white text-opacity-75 text-center border-gray-300 font-normal',
      },
    ],
  }));
  let headers;
  if (
    assignments &&
    assignments.length === 0 &&
    videos &&
    videos.length === 0
  ) {
    return (
      <p className="px-5 py-4 mt-6 text-2xl leading-8 bg-gray-100 border border-gray-500 border-dashed w-100 rounded-xl">
        No resources to display.
      </p>
    );
  }
  const allHeaders = Array.prototype.concat(
    assignmentHeaders,
    videoHeaders,
    sppHeaders,
  );
  const allSortedHeaders = _.sortBy(allHeaders, (item) => item.startDate);
  headers = Array.prototype.concat(studentHeaders, allSortedHeaders);

  const ISAAssignments = assignments.filter((a) => a.is_isa);
  let rowsNormalized = rowsData.map((item) => {
    const studentNotInISA = ISAAssignments.filter(
      (a) =>
        a.isa_student_list && !a.isa_student_list.includes(item.student.id),
    );
    return {
      studentName: `${item.student.first_name} ${item.student.last_name}`,
      id: item.student.id,
      ...Object.keys(item.assignment_data || []).reduce(
        (accum, assignment) => ({
          ...accum,
          [`a-${assignment}`]: {
            score: item.assignment_data[assignment].score,
            tier: item.assignment_data[assignment].tier,
            scoreType: item.assignment_data[assignment].score_type,
            isSubmitted: !!item.assignment_data[assignment].is_submitted,
            isSpp: get(assignmentdById, `${assignment}.is_spp`, false),
            isAssignment: true,
          },
        }),
        {},
      ),
      // fill up isa assignment with 'n/a'
      ...(studentNotInISA || []).reduce(
        (accum, ISAAssignment) => ({
          ...accum,
          [`a-${ISAAssignment.id}`]: {
            isNa: true,
          },
        }),
        {},
      ),
      ...(item.video_data || []).reduce(
        (accum, video) => ({
          ...accum,
          [`dv-${video.video_id}`]: {
            isVideo: true,
            score: video.completed_view_at
              ? `Watched${(parseFloat(video.watched_percentage) * 100).toFixed(
                  0,
                )}%`
              : `${(parseFloat(video.watched_percentage) * 100).toFixed(0)}%`,
          },
        }),
        {},
      ),
    };
  });

  const { Table, TableBody, TableRow, TableColumns, TableHeader, tableItems } =
    useTable({
      data: rowsNormalized,
      headers,
      sortable: false,
    });

  const renderRow = React.useCallback((row) => {
    const cells = row.cells.map((item, index) => {
      const { value, id: itemId } = item || {};
      // student name
      if (index === 0) {
        return CustomCell(
          `${itemId}-${index}`,
          value,
          'border-r border-b border-gray-300 text-gray-600',
        );
      }
      const customClassName = 'border-r border-b border-gray-300 text-center';
      const clickableClassName =
        'cursor-pointer assignment-header-wrapper hover:underline';
      // assignment
      const { isAssignment, isSpp, isNa, isVideo } = value || {};
      if (isNa) {
        const noResultCellValue = <div>n/a</div>;
        return CustomCell(
          `${itemId}-${index}`,
          noResultCellValue,
          customClassName,
        );
      }

      if (isAssignment && isSpp) {
        const { isSubmitted, score } = value || {};
        let sppColor;
        if (isSubmitted) {
          sppColor = 'bg-purple-700 text-purple-100';
        } else if (score >= 0) {
          sppColor = 'bg-purple-200 text-purple-800';
        }
        const sppCellValue = (
          <div className="font-normal text-16">{Math.round(score * 100)}%</div>
        );

        const { redirectUrl, state } = getassignmentCellRedirectUrl(item);
        return CustomCell(
          `${itemId}-${index}`,
          sppCellValue,
          `${customClassName} ${
            isSubmitted ? clickableClassName : ''
          } ${sppColor}`,
          router,
          isSubmitted ? redirectUrl : '',
          isSubmitted ? state : {},
        );
      }

      if (isAssignment) {
        const { score, tier, scoreType } = value || {};
        const tierColor = getTierColorClasses(performanceTiers, tier);

        const showScoreIcon = ['student', 'both'].includes(scoreType);
        const cellValue = (
          <div className="flex justify-center font-normal text-16">
            <div className={cn({ 'ml-6 mr-2 mt-1': showScoreIcon })}>
              {score}
            </div>
            {showScoreIcon && (
              <div>
                <ScoredIcon
                  teacherScored={
                    ['teacher', 'both'].includes(scoreType) ? 1 : 0
                  }
                  studentScored={
                    ['student', 'both'].includes(scoreType) ? 1 : 0
                  }
                  teacherAndStudentScored={scoreType === 'both' ? 1 : 0}
                  hideToolTip
                />
              </div>
            )}
          </div>
        );
        const { redirectUrl } = getassignmentCellRedirectUrl(item);
        return CustomCell(
          `${itemId}-${index}`,
          cellValue,
          tierColor
            ? `${customClassName} ${clickableClassName} ${tierColor}`
            : customClassName,
          router,
          tierColor ? redirectUrl : '',
        );
      }
      // daily video
      if (isVideo) {
        const { score } = value || {};
        let videoColor;
        if (score && score.includes('Watched')) {
          videoColor = 'bg-purple-700 text-purple-100';
        } else if (score) {
          videoColor = 'bg-purple-200 text-purple-800';
        }
        const videoCellValue = (
          <div className="font-normal text-16">
            {score && score.replace('Watched', '')}
          </div>
        );
        return CustomCell(
          `${itemId}-${index}`,
          videoCellValue,
          videoColor ? `${customClassName} ${videoColor}` : customClassName,
        );
      }

      return CustomCell('', '', customClassName);
    });

    return (
      <TableRow isDisabled row={row} key={row.id}>
        <TableColumns cells={cells} />
      </TableRow>
    );
  }, []);

  return (
    <div className="pb-6">
      <Table className="my-16 border sticky-table">
        <TableHeader style={{ maxWidth: '250px' }} showSortIcon={false} />
        <TableBody>
          {tableItems.map((item, index) => renderRow(item, index))}
        </TableBody>
      </Table>
    </div>
  );
};

export default Table;
